'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  RiArrowDownSLine,
  RiArticleLine,
  RiHistoryLine,
  RiMegaphoneLine,
  RiQuestionLine,
  RiRobot2Line,
  RiPaletteLine,
  RiSteering2Line,
} from '@remixicon/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { RiArrowRightSLine } from '@/components/ui/rtl-icons';
import { Logo } from '@/components/logo';
import { Link, usePathname } from '@/i18n/navigation';
import { dur, ease, sceneContainer, sceneItem, spring, springFade } from '@/lib/motion';

/** Locale'i koruyan Link + Motion. `motion.create` motion@13'ün API'si. */
const MotionLink = motion.create(Link);

/**
 * Site header + açılır menü.
 *
 * Dış kabuk (.menuTop.header → header.container) wask.co'dan geliyor; genişliği
 * hero'nun orta sütunuyla BİREBİR aynı (globals.css "2) Header hizalama").
 * Menünün kendisi bizim.
 *
 * ---------------------------------------------------------------------------
 * Panelin şekli
 * ---------------------------------------------------------------------------
 * Panel içerik sütununu KAPLAMIYOR: en fazla 700px'lik (dar ekranda sütuna
 * göre kısalan) bağımsız bir kart, tetikleyicinin altında ortalanmış.
 *
 * İçi 56/44 iki sütun ve iki satır: üst satırda sütun etiketleri
 * (ÜRÜN / ÖNE ÇIKAN), alt satırda solda tek satırlık maddeler, sağda üzerinde
 * durulan maddeyi bir cümleyle anlatan kutu. Kutunun boyu listeden geliyor —
 * sabit değil, ızgaranın `1fr`i.
 *
 * 🔴 17 Eyl 2026 (kullanıcı kararı: "daha kurumsal, sade"): sağdaki KOYU
 * tuval kaldırıldı. Orada ürünün ekran görüntüsü duracaktı ama hiçbir maddede
 * görsel yoktu ve menü her açıldığında boş bir ızgara dokusu görünüyordu —
 * ürün tanıtan bir menüde en pahalı boş alan orasıydı. Yerini panelle aynı
 * zeminden doğan, her zaman dolu bir özet kutusu aldı. Açıklama metni de
 * satırlardan oraya taşındı: liste artık yalnızca adları taşıyor, tek bir
 * açıklama okunuyor — ikisi birden değil.
 *
 * ---------------------------------------------------------------------------
 * Neden CSS değil de ölçüm
 * ---------------------------------------------------------------------------
 * Panelin genişliği ve yatay konumu AYNI ölçümden çıkmak zorunda: konum
 * `tetikleyicinin ortası − genişlik / 2`. Genişlik CSS'te, konum JS'te
 * dursaydı ikisi ayrışır ve panel tetikleyicinin altına oturmazdı. Bu yüzden
 * ikisi de burada hesaplanıp satır içi veriliyor (`geom`).
 *
 * Konum `left` ile değil `x` ile sürülüyor — DESIGN.md §1: yalnızca
 * transform / opacity / filter animasyonlanır. Bunun ikinci faydası: menüden
 * menüye geçerken panel kapanıp açılmıyor, yatayda KAYIYOR. Hareket bir şey
 * anlatıyor — "aynı panel, başka bölüm".
 *
 * ---------------------------------------------------------------------------
 * Hareket (DESIGN.md §5)
 * ---------------------------------------------------------------------------
 *   • Açılış   → YOK. Panel ilk karede son hâlinde (kullanıcı kararı,
 *                9 Eyl 2026): menü de sayfa gibi "yüklenmiş" gelsin.
 *   • Geçiş    → menüden menüye panel `x` ile KAYIYOR, kapanıp açılmıyor.
 *                Kalan tek yatay hareket bu ve bir şey anlatıyor: "aynı
 *                panel, başka bölüm".
 *   • Satırlar → etkin satırın zemini YERİNDE açılıyor (opaklık + ufak
 *                ölçek, `spring.snap`); tek bir zemini `layoutId` ile satırdan
 *                satıra taşımıyoruz.
 *   • Özet     → sağdaki kutunun zemini, çerçevesi ve eteği SABİT; yalnızca
 *                içindeki metin değişiyor, o da anında. Kutu da kaysaydı
 *                hangi maddeye baktığınız değil kutunun kendisi dikkat
 *                çekerdi.
 *
 * Karartma katmanı kaldırıldı: bir gezinme menüsü için tüm sayfayı
 * karartmak modal ağırlığı demek (DESIGN.md §4 "ağırlık anlam taşır").
 *
 * Fare niyeti (hover intent): açılış 70 ms, kapanış 180 ms gecikmeli.
 */

type MenuId = 'features' | 'resources';

type MegaItem = {
  key: string;
  href: string;
  icon: ReactNode;
  /** Başlığın yanında AI rozeti — yapay zekâ destekli modüller */
  ai?: boolean;
};

/* Maddenin açıklaması burada DEĞİL, çeviri dosyasında iki ayrı anahtarda:
   `menu.<key>.desc` mobil çekmecedeki kısa satır, `menu.<key>.long` masaüstü
   panelindeki özet kutusunun cümlesi. İkisi ayrı çünkü biri bir satıra,
   öteki üç satıra yazılıyor — tek metni iki yere sığdırmaya çalışmak ya
   çekmeceyi uzatıyor ya kutuyu yarım bırakıyordu. */

/* Tuple tipi, dizi değil. `noUncheckedIndexedAccess` açık olduğu için
   `MENUS[0]` ve `items[0]` normalde `T | undefined` dönüyor ve her kullanımda
   kontrol istiyor. Gerçekte tam iki menü var ve ikisi de boş değil — bunu
   tipe yazınca hem `!` gerekmiyor hem de birinin boşaltılması derleme hatası
   veriyor. */
type MegaMenu = {
  id: MenuId;
  items: readonly [MegaItem, ...MegaItem[]];
};

const MENUS: readonly [MegaMenu, MegaMenu] = [
  {
    id: 'features',
    items: [
      /* 14 Eki 2026: dört sayfa da yayında. "AI Otomasyon" (`/features/workflow`)
         → "AI Otopilot": kural ekranları 5 Eki'de kalktı, yerini AI operatör
         aldı. "Analiz" (`/features/analyze`) → "Kampanyalar": panelin Analiz
         bölümü 24 Eyl'de kalktı. Eski iki adres hiç yayına girmemişti. */
      { key: 'agent', href: '/features/agent', icon: <IconAgent />, ai: true },
      {
        key: 'autopilot',
        href: '/features/autopilot',
        icon: <IconAutopilot />,
        ai: true,
      },
      {
        key: 'creative',
        href: '/features/creative',
        icon: <IconCreative />,
        ai: true,
      },
      { key: 'campaigns', href: '/features/campaigns', icon: <IconCampaigns /> },
    ],
  },
  {
    id: 'resources',
    items: [
      { key: 'blog', href: '/blog', icon: <IconBlog /> },
      { key: 'changelog', href: '/changelog', icon: <IconChangelog /> },
      /* 🔴 ÜÇ kalem — eskiden dörttü. "Dokümantasyon" (`/docs`) 18 Eyl
         2026'da "Yardım Merkezi"yle BİRLEŞTİ (kullanıcı kararı): ikisi de
         "bu sistem nasıl kullanılıyor" sorusuna cevap veriyordu ve menüde
         yan yana durmaları, cevabı arayana hangi kapıdan gireceğini
         sorduruyordu. Gerekçenin uzunu `lib/site-links.ts`te.

         Boşalan yer DOLDURULMADI. Liste `1fr` akıyor ve üç satırla da
         hizası bozulmuyor; dördüncü bir kalem uydurmak menüyü yeniden
         dörde tamamlamak için içerik icat etmek olurdu. Geliştirici
         dokümanı buraya girmedi çünkü mutlak adres (ayrı alan adı) ve bu
         liste `Link` ile basılıyor — dil önekini o ekliyor. */
      { key: 'help', href: '/help', icon: <IconHelp /> },
    ],
  },
];

/** Üst gezinmedeki görünüm sırası — ekran görüntüsündeki gibi. */
const NAV: readonly (MenuId | 'pricing')[] = ['features', 'pricing', 'resources'];

const OPEN_DELAY = 70;
const CLOSE_DELAY = 180;

/** Panelin tavan genişliği. Dar ekranda içerik sütununa göre kısalıyor.
    800'den 700'e indi: satırlar tek satıra inince liste daraldı, eski
    genişlikte kutu da liste de gereksiz yayılıyordu. */
const PANEL_MAX = 700;
/** Panelin içerik sütununun kenarına yapışmaması için bırakılan pay. */
const PANEL_GUTTER = 12;
/** .mega-caret'in kenar uzunluğu (globals.css) — ortalamak için yarısı gerekli. */
const CARET_SIZE = 12;

type Geometry = {
  /** Panel genişliği (px) — satır içi stile gidiyor */
  width: number;
  /** Menü başına panelin sol kenarının kapsayıcıya göre konumu */
  panelX: Partial<Record<MenuId, number>>;
  /** Menü başına tetikleyicinin yatay ortası, kapsayıcıya göre */
  caretX: Partial<Record<MenuId, number>>;
};

/**
 * Sayfanın header'ın ALTINDA kalan yüzeyi.
 *
 * `dark`  → sayfa `.dark-block` ile açılıyor (anasayfa). Header üst kenara
 *           yapışık koyu şerit olarak başlıyor, blok header'ın altından
 *           geçince beyaz kabuğa kopuyor.
 * `light` → sayfa açık zeminle açılıyor (/about gibi). İzlenecek koyu blok
 *           YOK, kabuk en baştan ayrık.
 *
 * Neden prop, neden `document.querySelector('.dark-block')` ile otomatik
 * değil: `stuck` sunucuda basılan ilk kareyi de belirliyor. Otomatik
 * algılamada sunucu her zaman koyu şeridi basar, hidrasyondan sonra efekt
 * kabuğa çevirir ve açık zeminli her sayfa yüklenirken tepede koyu bir şerit
 * yanıp söner. Prop, ilk kareyi baştan doğru basıyor.
 */
type Surface = 'dark' | 'light';

export function SiteHeader({ surface = 'dark' }: { surface?: Surface } = {}) {
  const t = useTranslations('header');
  const pathname = usePathname();

  const [openId, setOpenId] = useState<MenuId | null>(null);
  const [activeItem, setActiveItem] = useState<Record<MenuId, string>>({
    features: MENUS[0].items[0].key,
    resources: MENUS[1].items[0].key,
  });
  const [geom, setGeom] = useState<Geometry>({
    width: PANEL_MAX,
    panelX: {},
    caretX: {},
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<MenuId | null>('features');
  /** Koyu blok header'ın altından tamamen geçti mi — ayrık kabuk modu.
      Açık zeminli sayfada izlenecek blok yok: kabuk en baştan ayrık. */
  const [stuck, setStuck] = useState(surface === 'light');

  const containerRef = useRef<HTMLElement | null>(null);
  const triggerRefs = useRef<Partial<Record<MenuId, HTMLButtonElement | null>>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Klavyeyle (ok tuşu) açıldıysa panel DOM'a girer girmez ilk bağlantıya
     odaklan. Panel AnimatePresence ile mount olduğu için tetikleyicinin
     içinden doğrudan odaklanmak erken kalıyor — bayrak + effect gerekiyor. */
  const focusOnOpen = useRef(false);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const openMenu = useCallback(
    (id: MenuId, immediate = false) => {
      clearTimer();
      if (immediate) {
        setOpenId(id);
        return;
      }
      timer.current = setTimeout(() => setOpenId(id), OPEN_DELAY);
    },
    [clearTimer],
  );

  const closeMenu = useCallback(
    (immediate = false) => {
      clearTimer();
      if (immediate) {
        setOpenId(null);
        return;
      }
      timer.current = setTimeout(() => setOpenId(null), CLOSE_DELAY);
    },
    [clearTimer],
  );

  useEffect(() => clearTimer, [clearTimer]);

  /* Panelin genişliği + her tetikleyici için yatay konumu.
     Font geç yüklenirse düğme genişliği değişiyor, o yüzden fonts.ready'de de
     ölçülüyor. Ölçüm ilk boyamadan hemen sonra tamamlanıyor ve panel ancak
     kullanıcı etkileşimiyle açıldığı için açılışta yanlış konum görülmüyor. */
  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      if (!container) return;
      const base = container.getBoundingClientRect();
      const width = Math.min(PANEL_MAX, base.width - PANEL_GUTTER * 2);

      const panelX: Geometry['panelX'] = {};
      const caretX: Geometry['caretX'] = {};
      for (const menu of MENUS) {
        const trigger = triggerRefs.current[menu.id];
        if (!trigger) continue;
        const box = trigger.getBoundingClientRect();
        const center = box.left - base.left + box.width / 2;
        caretX[menu.id] = center;
        /* Ortala, ama içerik sütununun dışına taşma: dar masaüstünde
           "Kaynaklar" tetikleyicisi sağa yakın kalıyor ve ortalanmış panel
           sütunun sağını aşabiliyor. Aşarsa panel kenara yaslanır, ok
           işareti yine tetikleyicinin ortasında kalır. */
        panelX[menu.id] = Math.max(0, Math.min(center - width / 2, base.width - width));
      }
      setGeom({ width, panelX, caretX });
    };

    measure();
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure).catch(() => {});
    return () => window.removeEventListener('resize', measure);
  }, []);

  /* Koyu bloğun alt kenarı header'ın altından geçince ayrık moda geç.
     `scroll` dinleyicisi DEĞİL IntersectionObserver: kaydırma dinleyicisi her
     karede getBoundingClientRect okuyup düzen hesabı tetikler, IO ise eşiği
     tarayıcı tarafında izliyor ve yalnızca GEÇİŞTE bir kez ateşliyor.

     rootMargin header'ın yüksekliği kadar yukarıdan kısılıyor: kesişim
     penceresinin üst kenarı böylece header'ın ALT kenarına oturuyor, yani
     "koyu blok header'ın arkasından çıktı" anını yakalıyoruz. Yükseklik
     breakpoint'e göre değiştiği için gözlemci resize'da yeniden kuruluyor —
     rootMargin sonradan değiştirilemiyor.

     Karar `isIntersecting` ile değil boundingClientRect ile veriliyor: ilki
     blok viewport'un ALTINDA kaldığında da false döner (koyu bloğu olmayan
     bir sayfada header'ı sebepsiz ayırırdı), ikincisi hangi yönden çıktığını
     ayırt ediyor.

     Açık zeminli sayfada (surface="light") gözlemci hiç kurulmuyor: izlenecek
     blok yok ve `stuck` zaten baştan true. */
  useEffect(() => {
    if (surface === 'light') return;

    const dark = document.querySelector('.dark-block');
    if (!dark) return;

    let io: IntersectionObserver | null = null;
    const attach = () => {
      io?.disconnect();
      const h = containerRef.current?.offsetHeight ?? 0;
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry) setStuck(entry.boundingClientRect.bottom <= h);
        },
        { rootMargin: `-${Math.round(h)}px 0px 0px 0px`, threshold: 0 },
      );
      io.observe(dark);
    };

    attach();
    window.addEventListener('resize', attach);
    return () => {
      io?.disconnect();
      window.removeEventListener('resize', attach);
    };
  }, [surface]);

  /* Dışarı tıklama, Esc, sayfa kaydırma → kapat. */
  useEffect(() => {
    if (!openId) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeMenu(true);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      closeMenu(true);
      triggerRefs.current[openId]?.focus();
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeMenu(true);
    };
    const onScroll = () => closeMenu(true);

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('scroll', onScroll);
    };
  }, [openId, closeMenu]);

  /* Mobil çekmece açıkken arka plan kaymasın. */
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  /* Gezinme olunca her şey kapansın. */
  useEffect(() => {
    setOpenId(null);
    setMobileOpen(false);
  }, [pathname]);

  /* Ok tuşuyla açıldıysa odağı panelin ilk bağlantısına taşı. */
  useEffect(() => {
    if (!openId || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    containerRef.current?.querySelector<HTMLAnchorElement>('.mega-panel a')?.focus();
  }, [openId]);

  /* Fareye özel: dokunmatikte `pointerenter` de ateşleniyor ve hemen ardından
     `click` geliyor — parmak 70 ms'den uzun basılı kalırsa menü önce hover ile
     açılıyor, sonra tıklama onu kapatıyordu. Tablette menü "açılmıyor" gibi
     görünmesinin sebebi buydu. */
  const onMouse = (fn: () => void) => (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') fn();
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent, id: MenuId) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusOnOpen.current = true;
      openMenu(id, true);
    }
  };

  const openMenuData = openId ? MENUS.find((m) => m.id === openId) : undefined;
  /* Sağdaki özet kutusunun konusu. `activeItem` bir anahtar tutuyor; kutu
     maddenin kendisine (ikon, ai bayrağı) ihtiyaç duyduğu için burada nesneye
     çevriliyor. Menü değişiminin ilk karesinde anahtar hâlâ eski menüye ait
     olabildiği için `?? items[0]` şart — yoksa kutu bir kare boş kalır. */
  const openActive = openMenuData
    ? (openMenuData.items.find((i) => i.key === activeItem[openMenuData.id]) ??
      openMenuData.items[0])
    : undefined;
  const panelX = openId ? (geom.panelX[openId] ?? 0) : 0;
  const caretCenter = openId ? (geom.caretX[openId] ?? 0) : 0;
  /* Ok işaretinin panelin SOL KENARINA göre konumu. Panel kenara yaslandığında
     bu değer ortadan kayıyor — ok yine tetikleyiciyi gösteriyor. Aynı değer
     `transformOrigin` olarak da kullanılıyor: panel okun dibinden büyüyor. */
  const caretOffset = caretCenter - panelX;

  /* Mobil çekmece açıkken kabuk geri yapışıyor: çekmece üst kenardan tam
     genişlikte inen bir yüzey, header havada dururken altından çıkan çekmece
     iki ayrı yüzey gibi okunuyordu. Menü açıkken ikisi tek gövde. */
  const detached = stuck && !mobileOpen;

  return (
    <div className="menuTop header" data-stuck={detached ? 'true' : 'false'}>
      {/* Yapışık moddaki tam genişlikte koyu şerit. Ayrık moda geçerken
          yalnızca söner — rengi/köşesi animasyonlanmıyor (globals.css
          "İki zemin katmanı"). */}
      <motion.span
        className="header-bar"
        aria-hidden="true"
        initial={false}
        animate={{ opacity: detached ? 0 : 1 }}
        transition={{ duration: dur.fast, ease: ease.out }}
      />

      <header className="container" ref={containerRef} onPointerLeave={onMouse(() => closeMenu())}>
        {/* Ayrık kabuk. `y` bilinçli: kabuk üst kenardan KOPUYOR, o yüzden
            biraz yukarıdan iniyor; geri kaydırınca aynı yol tersine işliyor
            ve kabuk kenara geri yapışıyor. Ölçek yok — 1200px genişliğinde
            bir yüzeyde en ufak ölçek bile kenarları santimlerce oynatır ve
            "hassas" değil "esneyen" okunur (DESIGN.md §4). */}
        <motion.span
          className="header-island"
          aria-hidden="true"
          initial={false}
          animate={{ opacity: detached ? 1 : 0, y: detached ? 0 : -8 }}
          transition={springFade(spring.default)}
        />
        <MotionLink
          href="/"
          className="header-logo-link"
          onPointerEnter={onMouse(() => closeMenu())}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={spring.snap}
        >
          <Logo className="header-logo" />
        </MotionLink>

        <nav className="mega-nav" aria-label={t('nav')}>
          <ul className="mega-nav-list">
            {NAV.map((entry) => {
              /* Fiyatlandırma menü değil düz bağlantı, ama sırası menülerin
                 ARASINDA — bu yüzden MENUS üzerinden değil NAV üzerinden
                 dönülüyor. Sadece MENUS.map edip sonuna eklersek sıra
                 Özellikler / Kaynaklar / Fiyatlandırma'ya kayıyor. */
              if (entry === 'pricing') {
                return (
                  <li key="pricing" onPointerEnter={onMouse(() => closeMenu())}>
                    <MotionLink
                      href="/pricing"
                      className="mega-nav-link"
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.96 }}
                      transition={spring.snap}
                    >
                      {t('pricing')}
                    </MotionLink>
                  </li>
                );
              }

              return (
                <li key={entry} onPointerEnter={onMouse(() => openMenu(entry))}>
                  <motion.button
                    type="button"
                    className="mega-nav-trigger"
                    ref={(el) => {
                      triggerRefs.current[entry] = el;
                    }}
                    aria-expanded={openId === entry}
                    aria-controls="mega-panel"
                    aria-haspopup="true"
                    data-open={openId === entry ? 'true' : 'false'}
                    onClick={() => (openId === entry ? closeMenu(true) : openMenu(entry, true))}
                    onKeyDown={(e) => onTriggerKeyDown(e, entry)}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.96 }}
                    transition={spring.snap}
                  >
                    {t(entry)}
                    <motion.span
                      className="mega-nav-chevron"
                      animate={{ rotate: openId === entry ? 180 : 0 }}
                      transition={spring.snap}
                    >
                      <ChevronDown />
                    </motion.span>
                  </motion.button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="desktop-buttons-c" onPointerEnter={onMouse(() => closeMenu())}>
          <MotionLink
            href="/login"
            className="btn login-c-btn login-btn"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            transition={spring.snap}
          >
            {t('login')}
          </MotionLink>
          <MotionLink
            href="/register"
            className="btn secondary-btn chevron-right start-free-trial-btn"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            transition={spring.snap}
          >
            {t('getStarted')}
            <ChevronRight />
          </MotionLink>
        </div>

        <motion.button
          type="button"
          className={`nav-icon-c${mobileOpen ? ' open' : ''}`}
          aria-label={t('menuToggle')}
          aria-expanded={mobileOpen}
          aria-controls="mobile-drawer"
          onClick={() => setMobileOpen((v) => !v)}
          whileTap={{ scale: 0.9 }}
          transition={spring.snap}
        >
          <span />
          <span />
          <span />
        </motion.button>

        {/* ---------- Açılır panel ----------
            Tek `key` bilinçli: menüden menüye geçerken bu düğüm YERİNDE kalıyor,
            yalnızca `x` değişiyor. Böylece panel kapanıp yeniden açılmak yerine
            yatayda kayıyor. `key={openId}` yazılsaydı her geçiş bir çıkış +
            giriş animasyonu olurdu ve bağlantı kopardı. */}
        <AnimatePresence>
          {openMenuData && openActive ? (
            <motion.div
              key="flyout"
              className="mega-flyout"
              style={{
                width: geom.width,
                transformOrigin: `${caretOffset}px 0px`,
              }}
              onPointerEnter={clearTimer}
              /* Açılış ve kapanış hareketi YOK (kullanıcı kararı, 9 Eyl 2026).
                 Panel eskiden hafifçe eğilerek, aşağı kayarak ve bulanıklıktan
                 çözülerek geliyordu; site sahipleri menünün de sayfa gibi
                 "yüklenmiş" gelmesini istedi. `initial={false}` — panel ilk
                 karede son hâlinde, kapanışta `exit` olmadığı için anında
                 gidiyor.

                 Kalan tek hareket YATAY: menüden menüye geçerken `x` kayıyor.
                 O bir giriş değil, konum sürekliliği — yukarıdaki tek `key`
                 kararının tamamlayıcısı ve kaldırılırsa panel ışınlanır. */
              initial={false}
              animate={{ x: panelX }}
              transition={spring.default}
            >
              <motion.span
                className="mega-caret"
                aria-hidden="true"
                initial={{ rotate: 45, x: caretOffset - CARET_SIZE / 2 }}
                animate={{ rotate: 45, x: caretOffset - CARET_SIZE / 2 }}
                transition={spring.default}
              />

              <div
                className="mega-panel"
                id="mega-panel"
                role="group"
                aria-label={t(openMenuData.id)}
              >
                {/* Gövde menüye göre değişiyor, geçiş ANLIK. Eskiden
                    `AnimatePresence mode="popLayout"` ile çapraz sönerdi; menünün
                    tüm giriş ve geçiş hareketi 9 Eyl 2026'da kaldırıldı. `key`
                    duruyor — menü değişince gövde yeniden kuruluyor. */}
                <div key={openMenuData.id} className="mega-body">
                  {/* Sütun etiketleri ızgaranın ÜST SATIRI — kutuların içinde
                      değil. İçeride dursalardı iki farklı dolgudan doğan iki
                      farklı yükseklikte olurlar, tek bir taban çizgisine
                      oturmazlardı. Soldaki etiket tetikleyicinin adını
                      tekrarlamıyor: "Özellikler" → "ÜRÜN". Aynı kelimeyi iki
                      kez okutmak hiyerarşi kurmaz, yer kaplar. */}
                  <span className="mega-eyebrow">{t(`section.${openMenuData.id}`)}</span>
                  <span className="mega-eyebrow mega-eyebrow-feature" aria-hidden="true">
                    {t('highlight')}
                  </span>

                  {/* Hücreler kademeli değil, hep birlikte ve hareketsiz:
                      `sceneContainer` + `sceneItem` kademesi menünün geri
                      kalanıyla birlikte kaldırıldı (9 Eyl 2026). */}
                  <ul className="mega-list">
                    {openMenuData.items.map((item) => {
                      const isActive = activeItem[openMenuData.id] === item.key;
                      /* Hücrede `whileHover` yok: hareketi zemin katmanı
                         taşıyor. Hücrenin kendisini ayrıca ittirmek üst üste
                         iki hareket demek, "hassas" değil "kıpır kıpır"
                         okunur (DESIGN.md §4). */
                      return (
                        <li key={item.key}>
                          <MotionLink
                            href={item.href}
                            className="mega-item"
                            data-active={isActive ? 'true' : 'false'}
                            onPointerEnter={() =>
                              setActiveItem((s) => ({
                                ...s,
                                [openMenuData.id]: item.key,
                              }))
                            }
                            onFocus={() =>
                              setActiveItem((s) => ({
                                ...s,
                                [openMenuData.id]: item.key,
                              }))
                            }
                            whileTap={{ scale: 0.99 }}
                            transition={spring.snap}
                          >
                            {/* Zemin her hücrede HEP duruyor, yalnızca açılıp
                                kapanıyor. `layoutId` ile tek bir zemini
                                hücreler arasında taşımak dört satırlık listede
                                uzun bir yol demekti. Ölçek 0.985'ten geliyor:
                                fark edilmeyecek kadar küçük ama zeminin
                                "açıldığını" hissettiriyor. */}
                            <motion.span
                              className="mega-item-glow"
                              aria-hidden="true"
                              initial={false}
                              animate={{
                                opacity: isActive ? 1 : 0,
                                scale: isActive ? 1 : 0.985,
                              }}
                              transition={springFade(spring.snap, dur.instant)}
                            />
                            <span className="mega-item-icon" aria-hidden="true">
                              {item.icon}
                            </span>
                            <span className="mega-item-title">
                              {t(`menu.${item.key}.title`)}
                              {item.ai ? <span className="mega-badge">AI</span> : null}
                            </span>
                            <span className="mega-item-arrow" aria-hidden="true">
                              <ChevronRight />
                            </span>
                          </MotionLink>
                        </li>
                      );
                    })}
                  </ul>

                  {/* ---------- Sağ sütun: öne çıkan ----------
                      Kutu `aria-hidden`: taşıdığı her şey (ad, rozet, adres)
                      soldaki listede zaten var ve orada BAĞLANTI. İkinci kez
                      okutmak ekran okuyucuda aynı menüyü iki tur gezdirirdi.
                      "Keşfedin" de bu yüzden bağlantı değil — tıklanan şey
                      satırın kendisi.

                      `key` maddeye bağlı: React içerideki metni yenilesin
                      diye değil (zaten yeniler), maddeden maddeye geçerken
                      düğümün kimliği değişsin diye — ileride buraya bir geçiş
                      eklenirse tutunacağı yer burası. */}
                  <div className="mega-feature" aria-hidden="true">
                    <span className="mega-feature-title">
                      {t(`menu.${openActive.key}.title`)}
                      {openActive.ai ? <span className="mega-badge">AI</span> : null}
                    </span>
                    <p key={openActive.key} className="mega-feature-desc">
                      {t(`menu.${openActive.key}.long`)}
                    </p>
                    <span className="mega-feature-cta">
                      {t(`discover.${openMenuData.id}`)}
                      <ChevronRight />
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </header>

      {/* ---------- Mobil çekmece ----------
          Akordiyon `layout` ile açılıyor, `height` animasyonuyla değil
          (DESIGN.md §1): Motion boyut değişimini transform'a çeviriyor, her
          karede yeniden düzen hesabı yapılmıyor. `layout`'un çocukları da
          işaretli, yoksa açılırken içerik dikey olarak eziliyor. */}
      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            key="drawer"
            className="m-drawer"
            id="mobile-drawer"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: -12,
              transition: { duration: dur.fast, ease: ease.in },
            }}
            transition={springFade(spring.soft)}
          >
            <motion.nav
              className="m-drawer-inner"
              aria-label={t('nav')}
              variants={sceneContainer}
              initial="hidden"
              animate="show"
            >
              {MENUS.map((menu) => (
                <motion.section
                  className="m-section"
                  key={menu.id}
                  variants={sceneItem}
                  layout
                  transition={spring.default}
                >
                  <motion.button
                    type="button"
                    className="m-section-head"
                    aria-expanded={mobileSection === menu.id}
                    onClick={() => setMobileSection((s) => (s === menu.id ? null : menu.id))}
                    whileTap={{ scale: 0.98 }}
                    transition={spring.snap}
                  >
                    {t(menu.id)}
                    <motion.span
                      className="m-section-chevron"
                      animate={{ rotate: mobileSection === menu.id ? 180 : 0 }}
                      transition={spring.snap}
                    >
                      <ChevronDown />
                    </motion.span>
                  </motion.button>

                  <AnimatePresence initial={false}>
                    {mobileSection === menu.id ? (
                      <motion.ul
                        key="list"
                        className="m-section-list"
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                          opacity: 0,
                          transition: { duration: dur.instant },
                        }}
                        transition={{ duration: dur.fast, ease: ease.out }}
                      >
                        {menu.items.map((item) => (
                          <motion.li key={item.key} layout>
                            <MotionLink
                              href={item.href}
                              className="m-item"
                              onClick={() => setMobileOpen(false)}
                              whileTap={{ scale: 0.98 }}
                              transition={spring.snap}
                            >
                              <span className="mega-item-icon" aria-hidden="true">
                                {item.icon}
                              </span>
                              <span className="mega-item-copy">
                                <span className="mega-item-title">
                                  {t(`menu.${item.key}.title`)}
                                  {item.ai ? <span className="mega-badge">AI</span> : null}
                                </span>
                                <span className="mega-item-desc">{t(`menu.${item.key}.desc`)}</span>
                              </span>
                            </MotionLink>
                          </motion.li>
                        ))}
                      </motion.ul>
                    ) : null}
                  </AnimatePresence>
                </motion.section>
              ))}

              <motion.div variants={sceneItem} layout transition={spring.default}>
                <MotionLink
                  href="/pricing"
                  className="m-link"
                  onClick={() => setMobileOpen(false)}
                  whileTap={{ scale: 0.98 }}
                  transition={spring.snap}
                >
                  {t('pricing')}
                </MotionLink>
              </motion.div>

              <motion.div
                className="m-drawer-cta"
                variants={sceneItem}
                layout
                transition={spring.default}
              >
                <MotionLink
                  href="/login"
                  className="btn login-btn"
                  onClick={() => setMobileOpen(false)}
                  whileTap={{ scale: 0.97 }}
                  transition={spring.snap}
                >
                  {t('login')}
                </MotionLink>
                <MotionLink
                  href="/register"
                  className="btn start-free-trial-btn chevron-right"
                  onClick={() => setMobileOpen(false)}
                  whileTap={{ scale: 0.97 }}
                  transition={spring.snap}
                >
                  {t('getStarted')}
                  <ChevronRight />
                </MotionLink>
              </motion.div>
            </motion.nav>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   İkonlar — Remix Icon (@remixicon/react)
   ---------------------------------------------------------------------------
   3227 ikonluk tutarlı bir set, tek tek import edildiği için tree-shaking
   çalışıyor (webfont indirilmiyor, yalnızca kullanılanlar bundle'a giriyor).

   Sarmalayıcı isimleri korundu (IconAgent, IconAutopilot…) — çağıran taraf
   değişmedi ve ikon değiştirmek tek satırlık bir iş. `function` bildirimi
   kullanılıyor, `const` değil: MENUS dizisi dosyanın başında bunları çağırıyor
   ve yalnızca function bildirimleri hoist ediliyor.

   İkonlar kutu içinde DEĞİL (DESIGN.md §3) — doğrudan zemine oturuyorlar,
   tek gösterge renk. */

const ICON = 20;

function IconAgent() {
  return <RiRobot2Line size={ICON} aria-hidden="true" />;
}
function IconAutopilot() {
  return <RiSteering2Line size={ICON} aria-hidden="true" />;
}
function IconCreative() {
  return <RiPaletteLine size={ICON} aria-hidden="true" />;
}
function IconCampaigns() {
  return <RiMegaphoneLine size={ICON} aria-hidden="true" />;
}
function IconBlog() {
  return <RiArticleLine size={ICON} aria-hidden="true" />;
}
function IconChangelog() {
  return <RiHistoryLine size={ICON} aria-hidden="true" />;
}
function IconHelp() {
  return <RiQuestionLine size={ICON} aria-hidden="true" />;
}

function ChevronDown({ className, ...rest }: { className?: string; [key: string]: unknown }) {
  return <RiArrowDownSLine size={16} className={className} aria-hidden="true" {...rest} />;
}

function ChevronRight() {
  return <RiArrowRightSLine size={16} aria-hidden="true" />;
}
